/**
 * Readable labels for the codes the backend stores: policy keys, workflow types and
 * steps, domain history / outbox event types, audit actions, event sources, and one
 * naming convention for orders. Staff never see a raw constant: a known code uses its
 * translation (`common:labels.*`), an unknown one is turned into words by
 * `humanizeCode` ("DOMAIN_NAMESERVERS_CHANGED" -> "Domain nameservers changed").
 *
 * Use `useLabels()` inside components (re-renders on a language change); the plain
 * functions read the current language and suit code that already re-renders with it.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { formatDate } from './format';

type Lookup = (key: string, options?: Record<string, unknown>) => string;
type Code = string | null | undefined;

/** Shown for a missing code, matching the empty-cell dash used across the console. */
const EMPTY = '—';

/** Words kept in their usual spelling when a code is turned into words. */
const SPELLING: Readonly<Record<string, string>> = {
  API: 'API',
  AUTHINFO: 'AuthInfo',
  CCCD: 'CCCD',
  CCTLD: 'ccTLD',
  CLTRID: 'clTRID',
  DNS: 'DNS',
  DNSSEC: 'DNSSEC',
  DS: 'DS',
  EKYC: 'eKYC',
  EPP: 'EPP',
  GTLD: 'gTLD',
  HSTM: 'HSTM',
  HTTP: 'HTTP',
  ICANN: 'ICANN',
  ID: 'ID',
  IDN: 'IDN',
  IP: 'IP',
  KYC: 'KYC',
  MFA: 'MFA',
  NS: 'NS',
  OCR: 'OCR',
  RDAP: 'RDAP',
  RDE: 'RDE',
  ROID: 'ROID',
  SLA: 'SLA',
  SLO: 'SLO',
  SVTRID: 'svTRID',
  TLD: 'TLD',
  TLS: 'TLS',
  TOTP: 'TOTP',
  UDRP: 'UDRP',
  URL: 'URL',
  URS: 'URS',
  UTC: 'UTC',
  UUID: 'UUID',
  VAT: 'VAT',
  VN: 'VN',
  VNNIC: 'VNNIC',
  WHOIS: 'WHOIS',
};

/** Splits CamelCase ("DomainRenewalWorkflow"); single-case codes ("BEFORE_P60D", "await.registry") are left alone. */
function splitCamel(code: string, sep: string): string {
  if (!/[a-z]/.test(code) || !/[A-Z]/.test(code)) return code;
  return code.replace(/([a-z0-9])([A-Z])/g, `$1${sep}$2`).replace(/([A-Z]+)([A-Z][a-z])/g, `$1${sep}$2`);
}

/** "DOMAIN_NAMESERVERS_CHANGED", "domain.nameservers.update", "DomainRenewalWorkflow", "admin-api" -> words. */
function words(code: string): string[] {
  return splitCamel(code.trim(), ' ')
    .split(/[\s_.\-/]+/)
    .filter(Boolean);
}

/**
 * Whether `value` looks like a machine code ("VNNIC_SYNC_REJECTED", "domain.nameservers.update",
 * "standard-epp") rather than text a person wrote: ASCII letters, digits and `_ . : / -`, no
 * spaces. Only codes are translated or turned into words; free text is shown as written.
 */
export function isCode(value: string | null | undefined): value is string {
  return !!value && /^[A-Za-z0-9_.:/-]+$/.test(value.trim()) && value.trim().length <= 120;
}

/**
 * Any code as readable words in sentence case, keeping acronyms (EPP, VNNIC, gTLD...):
 * "VNNIC_SYNC_REJECTED" -> "VNNIC sync rejected", "await.registry" -> "Await registry".
 * The language-neutral fallback for codes without a translation; `—` for an empty code.
 * Text that is not a code (an operator's reason, a registry message) comes back verbatim:
 * "QA cập nhật tên giao dịch" must not become "Qa cập nhật tên giao dịch".
 */
export function humanizeCode(code: Code): string {
  if (!code) return EMPTY;
  if (!isCode(code)) return code;
  const parts = words(code);
  if (!parts.length) return code;
  return parts
    .map((w, i) => {
      const known = SPELLING[w.toUpperCase()];
      if (known) return known;
      const lower = w.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

/** Translation key for a code: UPPER_SNAKE, whatever spelling the backend used; `null` if it cannot be one. */
function codeKey(code: string): string | null {
  const key = splitCamel(code.trim(), '_')
    .replace(/[\s.\-/]+/g, '_')
    .toUpperCase();
  return /^[A-Z0-9_]+$/.test(key) ? key : null;
}

/** "DomainRenewalWorkflow" and "DOMAIN_RENEWAL" both -> "DOMAIN_RENEWAL". */
function workflowKey(type: string): string | null {
  return codeKey(type)?.replace(/_WORKFLOW$/, '') ?? null;
}

/** "domain.nameservers.changed.v1" / "DOMAIN_NAMESERVERS_CHANGED" -> "DOMAIN_NAMESERVERS_CHANGED". */
function eventKey(type: string): string | null {
  return codeKey(type.replace(/[._]v\d+$/i, ''));
}

/** Input for `orderLabel`: whatever the page knows about the order. */
export interface OrderLabelInput {
  /** The order's first (or the page's own) domain name, as stored. */
  first_domain?: string | null;
  /** All domain names of the order, when the page has them; the first one is used if `first_domain` is missing. */
  domains?: readonly string[] | null;
  /** Number of order items; the label shows the others as "+N". Defaults to the number of `domains`. */
  item_count?: number | null;
  created_at: string | null | undefined;
}

/** The label functions, bound to one translation function. */
export interface Labels {
  /** Same as the exported `humanizeCode`. */
  humanize: (code: Code) => string;
  /** "DOSSIER_REQUIREMENTS", "2026.1" -> "Yêu cầu hồ sơ · phiên bản 2026.1"; also accepts "KEY@version". */
  policy: (code: Code, version?: string | number | null) => string;
  /** "DOMAIN_RENEWAL" or "DomainRenewalWorkflow" -> "Gia hạn tên miền". */
  workflowType: (type: Code) => string;
  /** Current workflow step: "await_registry" or "await.registry" -> "Chờ registry phản hồi". */
  workflowStep: (step: Code) => string;
  /** Domain history / outbox event type: "DOMAIN_NAMESERVERS_CHANGED" or "domain.nameservers.changed.v1". */
  eventType: (type: Code) => string;
  /** Audit action: "domain.nameservers.update" -> "Cập nhật máy chủ tên". */
  auditAction: (action: Code) => string;
  /** Event source or system actor id ("admin-api", "domain-worker", "lifecycle") -> what staff call it. */
  source: (source: Code) => string;
  /** EPP command as stored ("domain_transfer_reject") -> "Từ chối chuyển đổi". */
  eppCommand: (operation: Code) => string;
  /** One name for an order everywhere: "example.vn +1 tên miền · 2026-09-12", or "Đơn hàng 2026-09-12". */
  order: (order: OrderLabelInput) => string;
}

function makeLabels(t: Lookup): Labels {
  const lookup = (group: string, key: string | null, fallback: string): string =>
    key ? t(`labels.${group}.${key}`, { ns: 'common', defaultValue: fallback }) : fallback;

  const policy: Labels['policy'] = (code, version) => {
    if (!code) return EMPTY;
    let key = code.trim();
    let ver = version === null || version === undefined || version === '' ? null : String(version);
    const at = key.indexOf('@');
    if (at > 0) {
      ver ??= key.slice(at + 1) || null;
      key = key.slice(0, at);
    }
    const name = lookup('policy', codeKey(key), humanizeCode(key));
    return ver ? t('labels.policyVersion', { ns: 'common', name, version: ver }) : name;
  };

  const workflowType: Labels['workflowType'] = (type) => {
    if (!type) return EMPTY;
    const key = workflowKey(type);
    return lookup('workflow', key, humanizeCode(key ?? type));
  };

  const workflowStep: Labels['workflowStep'] = (step) => (step ? lookup('workflowStep', codeKey(step), humanizeCode(step)) : EMPTY);

  const eventType: Labels['eventType'] = (type) => {
    if (!type) return EMPTY;
    const key = eventKey(type);
    return lookup('event', key, humanizeCode(key ?? type));
  };

  const auditAction: Labels['auditAction'] = (action) => (action ? lookup('audit', codeKey(action), humanizeCode(action)) : EMPTY);

  const source: Labels['source'] = (value) => {
    if (!value) return EMPTY;
    const key = codeKey(value);
    // Unknown background services still read as automatic processing, never as a service name.
    const fallback = key && /_WORKER$/.test(key) ? t('labels.source.WORKER', { ns: 'common' }) : humanizeCode(value);
    return lookup('source', key, fallback);
  };

  const eppCommand: Labels['eppCommand'] = (op) => (op ? lookup('epp', codeKey(op), humanizeCode(op)) : EMPTY);

  const order: Labels['order'] = ({ first_domain, domains, item_count, created_at }) => {
    const date = formatDate(created_at);
    const domain = first_domain || domains?.find(Boolean) || null;
    if (!domain) return t('labels.order.dateOnly', { ns: 'common', date });
    const total = item_count ?? (domains ? new Set(domains.filter(Boolean)).size : 1);
    const n = Math.max(0, total - 1);
    return n > 0 ? t('labels.order.withOthers', { ns: 'common', domain, n, date }) : t('labels.order.single', { ns: 'common', domain, date });
  };

  return { humanize: humanizeCode, policy, workflowType, workflowStep, eventType, auditAction, source, eppCommand, order };
}

/** Label functions that follow the active language (re-render on a language change). */
export function useLabels(): Labels {
  const { t } = useTranslation('common');
  return useMemo(() => makeLabels(t as unknown as Lookup), [t]);
}

/** Labels in the current language, for code outside React render (or already re-rendering with the language). */
const current = (): Labels => makeLabels(i18n.t.bind(i18n) as unknown as Lookup);

export const policyLabel: Labels['policy'] = (code, version) => current().policy(code, version);
export const workflowTypeLabel: Labels['workflowType'] = (type) => current().workflowType(type);
export const workflowStepLabel: Labels['workflowStep'] = (step) => current().workflowStep(step);
export const eventTypeLabel: Labels['eventType'] = (type) => current().eventType(type);
export const auditActionLabel: Labels['auditAction'] = (action) => current().auditAction(action);
export const sourceLabel: Labels['source'] = (source) => current().source(source);
export const eppCommandLabel: Labels['eppCommand'] = (op) => current().eppCommand(op);
export const orderLabel: Labels['order'] = (order) => current().order(order);
